import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useTranslations } from 'use-intl';
import { Mail, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { PendingEmailImportRow } from '@/components/messages/PendingEmailImportRow';
import type { EmailThreadCompanyConnection } from '@/types/emailThread';

interface PendingEmailImportsQueueProps<Connection extends EmailThreadCompanyConnection> {
  /** Fetches the queue; must be stable (wrap in `useCallback`) because a change reloads it. */
  load: () => Promise<Connection[]>;
  /** Which heading to show (`consultantMessages.imports`): one company's queue or every company's. */
  titleKey: 'pendingTitle' | 'pendingAllTitle';
  /** Set only for the all-companies queue: a company name badge per row. */
  companyNameOf?: (connection: Connection) => string;
  /** Opens the given thread in the main Messages pane — see {@link PendingEmailImportRow}'s doc
   * comment for why review always routes through here. */
  onOpenThread: (threadId: string) => void;
}

/**
 * The approval queue for imported email threads (issue #290) — the feature's core trust mechanism —
 * shared by the per-company queue ({@link PendingEmailImports}) and the platform-admin one that spans
 * every company ({@link PendingEmailImportsAllCompanies}), which differ only in what they load and
 * whether a company badge is shown. `EmailThreadService.approveConnection`/`rejectConnection` act on
 * a connection either way.
 *
 * Renders as a slim single-line trigger (not the full list) so a large queue doesn't grow the
 * Messages page unboundedly — the full list only appears inside a bounded, scrollable modal.
 */
export function PendingEmailImportsQueue<Connection extends EmailThreadCompanyConnection>({
  load,
  titleKey,
  companyNameOf,
  onOpenThread,
}: PendingEmailImportsQueueProps<Connection>) {
  const t = useTranslations('consultantMessages.imports');
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  // Loads once per mount; a caller whose queue depends on something (the company) remounts this by `key`.
  useEffect(() => {
    load()
      .then(setConnections)
      .catch(() => {
        // Tier-2-equivalent for this list — the rest of Messages stays usable even if this
        // section fails to load; a silent empty queue is preferable to blocking the page.
        setConnections([]);
      })
      .finally(() => setLoading(false));
  }, [load]);

  const decide = useCallback(
    async (connectionId: string, decision: 'approve' | 'reject') => {
      setActioningId(connectionId);
      try {
        const service = ServiceRegistry.getEmailThreadService();
        await (decision === 'approve' ? service.approveConnection(connectionId) : service.rejectConnection(connectionId));
        setConnections((prev) => prev.filter((c) => c.id !== connectionId));
        toast.success(t(decision === 'approve' ? 'approved' : 'rejected'));
      } catch {
        toast.error(t(decision === 'approve' ? 'approveFailed' : 'rejectFailed'));
      } finally {
        setActioningId(null);
      }
    },
    [t],
  );

  const handleOpenThread = (threadId: string) => {
    setOpen(false);
    onOpenThread(threadId);
  };

  if (loading) return null; // avoid a layout flash for the common "nothing pending" case
  if (connections.length === 0) return null;

  const hasCrossConnectionWarning = connections.some((c) => c.connectionCount > 1);
  const title = t(titleKey, { count: String(connections.length) });

  return (
    <>
      <Card className="mb-4 border-amber-200 bg-amber-50/50">
        <CardContent className="flex items-center justify-between gap-3 py-3">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-amber-700" />
            <h3 className="text-sm font-medium text-amber-900">{title}</h3>
            {hasCrossConnectionWarning && (
              <AlertTriangle className="h-4 w-4 text-destructive" aria-label={t('crossConnectionWarning')} />
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            {t('review')}
          </Button>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{t('dialogHint')}</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-3">
            <div className="space-y-2">
              {connections.map((connection) => (
                <PendingEmailImportRow
                  key={connection.id}
                  connection={connection}
                  companyName={companyNameOf?.(connection)}
                  actioning={actioningId === connection.id}
                  onApprove={() => decide(connection.id, 'approve')}
                  onReject={() => decide(connection.id, 'reject')}
                  onOpenThread={() => handleOpenThread(connection.thread_id)}
                />
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
}
